import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ProviderType, StreamType } from 'videoservice-openapi-react';
import { useGeozones } from 'features/geozone';
import { usePlatforms } from 'features/platform';
import { useBroadcastRestrictions } from 'entities/broadcast-customer-restrictions';
import { useSportsOptions } from 'entities/sport';
import { EElementSize } from 'shared/types';
import { FieldText, SelectorTab, TSelectorTabItem } from 'shared/ui';
import { DateFieldSingle } from 'shared/ui/date-field-single';
import { FieldTextArea } from 'shared/ui/field-text-area';
import { ETogglerType } from 'shared/ui/list-control';
import { LoadingIndicator } from 'shared/ui/loading-indicator';
import { Select } from 'shared/ui/select';
import { BroadcastModeration } from '../broadcast-moderation';
import { ManualBroadcastStyled } from './manual-broadcast.styled';
import { IManualBroadcastProps } from './manual-broadcast.types';

export const ManualBroadcast: FC<IManualBroadcastProps> = ({
  broadcast,
  onChange = () => {},
}) => {
  const [title, setTitle] = useState(broadcast?.title || '');
  const [streamType, setStreamType] = useState(
    broadcast?.streamType ?? StreamType.IFRAME,
  );
  const [streamUrl, setStreamUrl] = useState(broadcast?.streamUrl ?? '');
  const [sportId, setSportId] = useState(broadcast?.sportId);
  const [startDate, setStartDate] = useState(broadcast?.startDate);
  const [selectedPlatformIds, setSelectedPlatformIds] = useState<number[]>([]);
  const [selectedGeozoneIds, setSelectedGeozoneIds] = useState<number[]>([]);
  const [platformRestrictions, setPlatformRestrictions] = useState<number[]>(
    [],
  );
  const [geozoneRestrictions, setGeozoneRestrictions] = useState<number[]>([]);

  const sportsOptions = useSportsOptions();
  const { geozones, geozonesQuery } = useGeozones();
  const { platforms, platformsQuery } = usePlatforms();
  const { data, isLoading, isFetching } = useBroadcastRestrictions({
    broadcastId: broadcast?.id,
  });

  const canEdit = useMemo(
    () => !broadcast || broadcast?.providerType === ProviderType.MANUAL,
    [broadcast],
  );

  const restrictedGeozones = useMemo(
    () =>
      geozones
        .filter(el => !selectedGeozoneIds.includes(el.id))
        .map(el => el.id),
    [geozones, selectedGeozoneIds],
  );

  const restrictedPlatforms = useMemo(
    () =>
      platforms
        .filter(el => !selectedPlatformIds.includes(el.id))
        .map(el => el.id),
    [platforms, selectedPlatformIds],
  );

  useEffect(() => {
    setSelectedPlatformIds(
      platforms
        .filter(el => !platformRestrictions.includes(el.id))
        .map(el => el.id),
    );
  }, [platformRestrictions, platforms]);

  useEffect(() => {
    setSelectedGeozoneIds(
      geozones
        .filter(el => !geozoneRestrictions.includes(el.id))
        .map(el => el.id),
    );
  }, [geozoneRestrictions, geozones]);

  useEffect(() => {
    if (data) {
      setPlatformRestrictions(data.platformIds ?? []);
      setGeozoneRestrictions(data.geozoneIds ?? []);
    }
  }, [data]);

  useEffect(() => {
    onChange({
      geozoneIds: restrictedGeozones,
      platformIds: restrictedPlatforms,
      sportId: sportId,
      startDate,
      streamType,
      title,
      url: streamUrl,
    });
  }, [
    onChange,
    restrictedGeozones,
    restrictedPlatforms,
    sportId,
    startDate,
    streamType,
    streamUrl,
    title,
  ]);

  const dateChangeHandler = useCallback((date?: Date) => {
    setStartDate(date);
  }, []);

  const streamTypeOptions = useMemo<TSelectorTabItem<StreamType>[]>(
    () => [
      {
        caption: 'iframe',
        key: StreamType.IFRAME,
        value: StreamType.IFRAME,
      },
      {
        caption: 'hls',
        key: StreamType.HLS,
        value: StreamType.HLS,
      },
    ],
    [],
  );

  return (
    <ManualBroadcastStyled>
      <div className="input-block">
        <label className="input-label">
          Тип трансляции
          <SelectorTab<StreamType>
            value={streamType}
            options={streamTypeOptions}
            onSelect={setStreamType}
            disabled={!canEdit}
          />
        </label>
      </div>

      <div className="input-block">
        <label className="input-label">
          Название
          <FieldText
            placeholder="Введите название трансляции"
            value={title}
            onChange={e => setTitle(e.target.value)}
            $size={EElementSize.M}
            disabled={!canEdit}
          />
        </label>
      </div>

      <div className="input-block group">
        <label className="input-label">
          Вид спорта
          <Select<number | undefined>
            placeholder="Выберите вид спорта"
            options={sportsOptions}
            value={sportId}
            onChange={setSportId}
            onClear={() => setSportId(undefined)}
            width="440px"
            disabled={!canEdit}
          />
        </label>
        <label className="input-label">
          Дата и время начала
          <DateFieldSingle
            date={startDate}
            onDateChange={dateChangeHandler}
            placeholder="Выберите дату"
            disabled={!canEdit}
          />
        </label>
      </div>

      <div className="input-block">
        <label className="input-label">
          URL Трансляции
          <FieldTextArea
            placeholder="Введите ссылку на трансляцию"
            value={streamUrl}
            onChange={e => setStreamUrl(e.target.value)}
            disabled={!canEdit}
          />
        </label>
      </div>
      {!isFetching &&
        !isLoading &&
        !geozonesQuery.isLoading &&
        !platformsQuery.isLoading && (
          <>
            <BroadcastModeration
              items={platforms}
              selected={selectedPlatformIds}
              title="Доступ к трансляции на платформе"
              togglerType={ETogglerType.TOGGLE}
              onChange={setSelectedPlatformIds}
            />

            <BroadcastModeration
              items={geozones}
              selected={selectedGeozoneIds}
              title="Доступ для просмотра трансляции в гео-зонах"
              togglerType={ETogglerType.CHECKBOX}
              onChange={setSelectedGeozoneIds}
            />
          </>
        )}
      {(isLoading ||
        isFetching ||
        geozonesQuery.isLoading ||
        platformsQuery.isLoading) && <LoadingIndicator type="full" />}
    </ManualBroadcastStyled>
  );
};
